"use client";

import Image from "next/image";
import React from "react";

import { useTranslations } from "next-intl";

import IconCall from "@/icons/call.svg";
import IconBuildings from "@/icons/content/property/buildings.svg";
import { useCountryStore } from "@/store/country.store";
import { PhoneNumber } from "@/ui/PhoneNumber";
import { cn } from "@/utils/cn";

interface ObjectDetailManagerInfoProps {
  name: string;
  role: string;
  image: string;
  count: number;
  phone: string;
  className?: string;
}

export const ObjectDetailManagerInfo: React.FC<
  ObjectDetailManagerInfoProps
> = ({ name, role, image, count, phone, className }) => {
  const t = useTranslations();
  const { country } = useCountryStore();

  return (
    <div
      className={cn(
        "grid grid-cols-[auto_1fr] gap-x-4 gap-y-4",
        "md:grid-cols-[repeat(3,auto)] md:gap-6 md:items-center",
        "xl:gap-8",
        className,
      )}
    >
      {/* Image - First column on mobile and desktop */}
      <div className="flex justify-start row-span-2 md:row-span-1">
        <div className="relative w-40 h-40 rounded-sm overflow-hidden bg-gray-200">
          <Image
            src={image}
            alt={name}
            fill
            className="object-cover"
          />
        </div>
      </div>

      {/* Name and Role - Second column on desktop, part of mobile layout */}
      <div className="flex flex-col gap-4">
        <h3 className="text-lg font-medium text-dark">{name}</h3>
        <p className="text-md text-dark">{role}</p>
      </div>

      {/* Count and Phone - Third column on desktop, part of mobile layout */}
      <div className="flex flex-col gap-4 md:text-right xl:ps-8">
        <p className="text-md text-gray-800 flex items-center gap-4">
          <IconBuildings
            width={20}
            height={20}
            aria-hidden="true"
          />
          <span>{t("pluralization.property", { count })}</span>
        </p>
        <div className="flex items-center gap-4">
          <IconCall
            width={20}
            height={20}
            aria-hidden="true"
          />
          <PhoneNumber
            phone={phone}
            countryCode={country?.code || "US"}
            className="flex-1"
            showIcon={true}
          />
        </div>
      </div>
    </div>
  );
};

export default ObjectDetailManagerInfo;
